import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../service/auth';

@Component({
	selector: 'app-forgot-password',
	imports: [CommonModule, FormsModule],
	templateUrl: './forgot-password.html',
	styleUrl: './forgot-password.scss'
})
export class ForgotPasswordComponent {
	private authService = inject(AuthService);
	private router = inject(Router);

	// Form data
	email = '';

	// UI state
	isLoading = signal<boolean>(false);
	errorMessage = signal<string>('');
	successMessage = signal<string>('');

	// Signal to track if avatar is clicked (showing surprised state)
	private isSurprised = signal(false);

	// Computed property to determine which avatar image to display
	avatarImage = computed(() => {
		// Show surprised image when clicked
		if (this.isSurprised()) {
			return 'assets/images/avatar/Crustopher-Surprised.png';
		}
		return 'assets/images/avatar/Crustopher-Happy.png';
	});

	/**
	 * Handle form submission for password reset
	 */
	async onSubmit(): Promise<void> {
		// Reset messages
		this.errorMessage.set('');
		this.successMessage.set('');

		// Validate form
		if (!this.validateForm()) {
			return;
		}

		this.isLoading.set(true);

		try {
			await this.authService.resetPassword(this.email);
			this.successMessage.set('Password reset email sent! Check your inbox and click the link to reset your password.');
		} catch (error: any) {
			this.errorMessage.set(error);
		} finally {
			this.isLoading.set(false);
		}
	}

	/**
	 * Validate form inputs
	 */
	private validateForm(): boolean {
		if (!this.email.trim()) {
			this.errorMessage.set('Please enter your email address.');
			return false;
		}

		if (!this.isValidEmail(this.email)) {
			this.errorMessage.set('Please enter a valid email address.');
			return false;
		}

		return true;
	}

	/**
	 * Validate email format
	 */
	private isValidEmail(email: string): boolean {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return emailRegex.test(email);
	}

	/**
	 * Navigate to login page
	 */
	goToLogin(): void {
		this.router.navigate(['/auth/login']);
	}

	/**
	 * Navigate to signup page
	 */
	goToSignup(): void {
		this.router.navigate(['/auth/signup']);
	}

	// Handle avatar click to show surprised state
	onAvatarClick() {
		this.isSurprised.set(true);
		// Revert back to happy state after 500ms
		setTimeout(() => {
			this.isSurprised.set(false);
		}, 500);
	}
}
